import React, { useState, useMemo } from 'react';
import { useIntl, FormattedMessage } from 'react-intl';

import { filterData, formatValue } from '@ts-core/utils/dataTableUtils';

import Box from '@mui/material/Box';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';

import AnswerResultIcon from '@ts-core/core/components/AnswerResultIcon';

import TSTable from '@ts-core/components/Table/TSTable';
import { TSTableColumn } from '@ts-core/components/Table/TSTable/TSTable';

import { RankingStudent, RankingItem } from '../../types';

interface AssignmentsRankingTableProps {
  downloadFileName?: string;
  datesPicker?: React.ReactNode;
  studentsMode?: boolean;
  students?: RankingStudent[];
  assignments?: RankingItem[];
  error?: string;
  loading?: boolean;
  getRankingByAssignments?: () => void;
}

const AssignmentsRankingTable = (props: AssignmentsRankingTableProps) => {
  const intl = useIntl();

  const {
    downloadFileName,
    datesPicker,
    studentsMode = false,
    getRankingByAssignments,
    loading = false,
    error: errorMessage,
    students = [],
    assignments = [],
  } = props;

  const [query, setQuery] = useState('');
  const [showOnlyNoAnswerStudents, setShowOnlyNoAnswerStudents] = useState(false);
  const [selectedAssignment, setSelectedAssignment] = useState('all');

  const extraFilters = useMemo(() => {
    return [
      {
        component: (
          <FormControlLabel
            key="students"
            control={<Checkbox checked={showOnlyNoAnswerStudents} onChange={(event) => setShowOnlyNoAnswerStudents(event.target.checked)} />}
            label={<FormattedMessage id="assignmentResultsTable.filter.onlyStudentsWithoutAnswers" />}
          />
        ),
      },
    ];
  }, [showOnlyNoAnswerStudents]);

  const enabledAssignments = useMemo(() => {
    return selectedAssignment === 'all' ? assignments : assignments.filter((a) => a.id === selectedAssignment);
  }, [assignments, selectedAssignment]);

  const columns = useMemo((): TSTableColumn<RankingStudent>[] => {
    const result: TSTableColumn<RankingStudent>[] = [
      {
        Header: intl.formatMessage({ id: 'assignmentResultsTable.student' }),
        value: intl.formatMessage({ id: 'assignmentResultsTable.student' }),
        accessor: 'name',
        sortable: true,
        Footer: intl.formatMessage({ id: 'averages' }),
      },
    ];

    if (students.some((s) => s.group)) {
      result.push({
        Header: intl.formatMessage({ id: 'studentsTable.column.group' }),
        value: intl.formatMessage({ id: 'studentsTable.column.group' }),
        accessor: 'group',
        id: 'group',
        sortable: true,
        filterable: true,
      });
    }

    enabledAssignments.forEach((assignment) => {
      const { stats } = assignment;
      result.push({
        Header: assignment.name,
        value: assignment.name,
        accessor: `scores.${assignment.id}`,
        valueTransformer: (value) => (value !== undefined ? formatValue(value) : <AnswerResultIcon type="none" />),
        sortable: true,
        Footer: formatValue(stats.averageScore, stats.maxScore),
      });
    });

    return result;
  }, [intl, students, enabledAssignments]);

  const keys = ['name', 'group'];

  let filteredData = filterData(students, query, keys);
  if (showOnlyNoAnswerStudents) {
    filteredData = filteredData.filter((s: RankingStudent) => {
      return !enabledAssignments.some((a) => s.scores && s.scores[a.id] !== undefined);
    });
  }

  const title = (
    <Box display="flex" flexDirection="column">
      {datesPicker}
      <Box mt={2} ml={-0.5}>
        <FormControl fullWidth size="small">
          <InputLabel>
            <FormattedMessage id="assignments" />
          </InputLabel>
          <Select
            label={<FormattedMessage id="assignments" />}
            value={selectedAssignment || 'all'}
            fullWidth
            onChange={(event) => setSelectedAssignment(event.target.value)}
          >
            <MenuItem value="all">
              <FormattedMessage id="ranking.assignmentsFilters.all" />
            </MenuItem>
            {assignments.map((assignment, index) => (
              <MenuItem key={index} value={assignment.id}>
                {assignment.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>
    </Box>
  );

  return (
    <TSTable
      title={!studentsMode && title}
      data={filteredData}
      columns={columns}
      onSearch={(text) => setQuery(text)}
      allowDownloadAsXLSX={!studentsMode}
      loading={loading}
      errorMessage={errorMessage}
      xlsxFileName={downloadFileName}
      extraFilters={studentsMode ? [] : extraFilters}
      onRefreshData={getRankingByAssignments}
    />
  );
};

export default AssignmentsRankingTable;
